import { useState, useRef, useEffect } from "react";
import { SvgImg } from ".";
import { svgs } from "../consts";
import { ColorPalleteSVG, MenuSVG } from "./SVG";

const SvgOptionsMenu = ({
  currentIcon,
  highlightColor,
  textColor,
  onStyleChange,
}) => {
  return (
    <div className="mt-2 p-4 w-full rounded-md shadow-lg bg-white">
      <div className="space-y-4">
        <div>
          <IconSelection
            currentIcon={currentIcon}
            highlightColor={highlightColor}
            textColor={textColor}
            onIconSelect={(icon) => onStyleChange({ icon })}
          />
        </div>
        <div className="flex items-center">
          <input
            type="color"
            value={highlightColor}
            onChange={(e) => onStyleChange({ highlightColor: e.target.value })}
            className="sr-only"
            id="colorPicker"
          />
          <label
            htmlFor="colorPicker"
            className="p-1 cursor-pointer hover:bg-gray-100 rounded"
            title="Change color"
          >
            <ColorPalleteSVG className="w-5 h-5" />
          </label>
        </div>
      </div>
    </div>
  );
};

const EditableSvg = ({
  icon,
  highlightColor,
  textColor,
  className = "",
  scale = false,
  isEditable = false,
  onStyleChange,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [optionsPosition, setOptionsPosition] = useState({ left: 0, top: 0 });
  const optionsRef = useRef(null);
  const buttonRef = useRef(null);

  const updateOptionsPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOptionsPosition({
        top: rect.bottom + 60,
        left: window.innerWidth / 2,
      });
    }
  };

  const handleButtonClick = () => {
    setShowOptions((prev) => !prev);
    updateOptionsPosition();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showOptions &&
        optionsRef.current &&
        !optionsRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowOptions(false);
      }
    };

    const handleResize = () => {
      if (showOptions) {
        updateOptionsPosition();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, [showOptions]);

  return (
    <div className="group relative">
      <div
        className={isEditable ? "border-2 border-dashed border-gray-200" : ""}
      >
        <SvgImg
          src={svgs[icon]}
          highlightColor={highlightColor}
          textColor={textColor}
          className={className}
          scale={scale}
        />
      </div>

      {isEditable && (
        <>
          <button
            ref={buttonRef}
            onClick={handleButtonClick}
            className="absolute -right-8 top-0 opacity-0 group-hover:opacity-100 transition-opacity bg-teal-600 rounded-full"
          >
            <MenuSVG />
          </button>

          {/* Options Menu */}
          {showOptions && (
            <div
              ref={optionsRef}
              className="fixed w-72 bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-50 transform -translate-x-1/2"
              style={{
                left: `${optionsPosition.left}px`,
                top: `${optionsPosition.top}px`,
              }}
            >
              <SvgOptionsMenu
                currentIcon={icon}
                highlightColor={highlightColor}
                textColor={textColor}
                onStyleChange={onStyleChange}
                onClose={() => setShowOptions(false)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
const IconSelection = ({
  currentIcon,
  highlightColor,
  textColor,
  onIconSelect,
  showLabel = false,
  labelIndex,
  onClearIcon,
}) => {
  const containerRef = useRef(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
  const ITEM_WIDTH = 90; // Icon width + padding
  const BUFFER_SIZE = 10; // Number of extra items to render before and after visible items

  const handleScroll = () => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;

    const startIndex = Math.max(
      0,
      Math.floor(scrollLeft / ITEM_WIDTH) - BUFFER_SIZE,
    );
    const endIndex = Math.min(
      Object.keys(svgs).length,
      Math.ceil((scrollLeft + containerWidth) / ITEM_WIDTH) + BUFFER_SIZE,
    );

    setVisibleRange({ start: startIndex, end: endIndex });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial calculation

      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const svgEntries = Object.entries(svgs);
  const visibleIcons = svgEntries.slice(visibleRange.start, visibleRange.end);

  return (
    <>
      {showLabel && (
        <>
          Ikona {labelIndex + 1}
          {currentIcon && (
            <div className="absolute end-8 sm:end-4 mt-1">
              <button
                type="button"
                onClick={onClearIcon}
                className="text-red-500 hover:text-red-700 text-3xl"
              >
                &times;
              </button>
            </div>
          )}
        </>
      )}
      <div
        ref={containerRef}
        className="p-2 h-32 flex flex-row w-full space-x-2 border border-gray-200 rounded-lg whitespace-nowrap overflow-x-auto 
                  sm:[&::-webkit-scrollbar]:h-[0.6rem] sm:[&::-webkit-scrollbar-track]:bg-gray-100 sm:[&::-webkit-scrollbar-thumb]:bg-gray-300 sm:[&::-webkit-scrollbar-track]:rounded-full sm:[&::-webkit-scrollbar-thumb]:rounded-full"
        style={{
          position: "relative",
        }}
      >
        <div
          style={{
            width: `${svgEntries.length * ITEM_WIDTH}px`,
            height: "50px",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />
        <div
          style={{
            transform: `translateX(${visibleRange.start * ITEM_WIDTH}px)`,
            display: "flex",
            gap: "1rem",
            position: "absolute",
            left: 0,
            top: "0.5rem",
          }}
        >
          {visibleIcons.map(([key, src]) => (
            <div
              key={key}
              name={key}
              className="cursor-pointer flex-shrink-0"
              onClick={() => onIconSelect(String(src.slice(5, 8)))}
              style={{
                width: `${ITEM_WIDTH}px`,
              }}
            >
              <SvgImg
                src={src}
                selected={currentIcon === src.slice(5, 8)}
                displayBorder={true}
                highlightColor={highlightColor}
                textColor={textColor}
                style={{
                  maxWidth: "50px",
                  maxHeight: "50px",
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default EditableSvg;
